import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Button,
  Space,
  Modal,
  message,
  Descriptions,
  Drawer,
} from "antd";
import { Table } from "ant-table-extensions";
import http from "../../helpers/http";
import helper from "../../helpers/helper";
import config from "../../config/config";
import { ExclamationCircleOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";

const { confirm } = Modal;

function RetailerList() {
  const [loading, setLoading] = useState(false);
  const [datas, setDatas] = useState([]);
  const [data, setData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const moduleNamePural = "Retailer Applications";
  const base_url = config.api_url + "admin/retailer/applications";

  const fetchDatas = async () => {
    setLoading(true);
    try {
      const response = await http.get(base_url);
      if (response && response.data) {
        setDatas(response.data);
      }
    } catch (error) {
      message.error("Failed to fetch applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatas();
  }, []);

  const handleView = (record) => {
    setData(record);
    setDrawerOpen(true);
  };

  const handleDelete = (id) => {
    confirm({
      title: "Are you sure you want to delete this application?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await http.delete(`${base_url}/${id}`);
          message.success("Application deleted successfully");
          fetchDatas();
        } catch (error) {
          message.error("Failed to delete application");
        }
      },
    });
  };

  const fields = {
    first_name: "First Name",
    last_name: "Last Name",
    email: "Email",
    owner_manager: "Owner/Manager",
    company_name: "Company Name",
    website: "Website",
    phone: "Phone",
    abn_no: "ABN No.",
    address_1: "Address Line 1",
    address_2: "Address Line 2",
    city: "City",
    postal_code: "Postal Code",
    country: "Country",
    createdAt: {
      header: "Applied At",
      formatter: (_fieldValue, record) => {
        return `${helper.ISTDate(record.createdAt)}`;
      },
    },
  };

  const columns = [
    {
      title: "Full Name",
      key: "name",
      render: (text, record) => `${record.first_name} ${record.last_name}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Applied At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => helper.ISTDate(text),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb
        style={{ margin: "16px 0" }}
        items={[
          {
            title: "JagKris",
          },
          {
            title: moduleNamePural,
          },
        ]}
      />

      <Row>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <Card title={moduleNamePural} bordered={false}>
              <Table
                rowKey={(record) => record._id}
                columns={columns}
                dataSource={datas}
                loading={loading}
                exportableProps={{
                  showColumnPicker: false,
                  fields,
                  fileName: moduleNamePural,
                  children: "Export to CSV",
                }}
                searchable
              />
            </Card>
          </div>
        </Col>
      </Row>

      <Drawer
        title="Application Details"
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={600}
      >
        {data && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="First Name">{data.first_name}</Descriptions.Item>
            <Descriptions.Item label="Last Name">{data.last_name}</Descriptions.Item>
            <Descriptions.Item label="Email">{data.email}</Descriptions.Item>
            <Descriptions.Item label="Owner/Manager">{data.owner_manager}</Descriptions.Item>
            <Descriptions.Item label="Company Name">{data.company_name}</Descriptions.Item>
            <Descriptions.Item label="Website">{data.website || "NA"}</Descriptions.Item>
            <Descriptions.Item label="Phone">{data.phone}</Descriptions.Item>
            <Descriptions.Item label="ABN No.">{data.abn_no}</Descriptions.Item>
            <Descriptions.Item label="Address Line 1">{data.address_1}</Descriptions.Item>
            <Descriptions.Item label="Address Line 2">{data.address_2}</Descriptions.Item>
            <Descriptions.Item label="City">{data.city}</Descriptions.Item>
            <Descriptions.Item label="Postal Code">{data.postal_code}</Descriptions.Item>
            <Descriptions.Item label="Country">{data.country}</Descriptions.Item>
            <Descriptions.Item label="Applied At">{helper.ISTDate(data.createdAt)}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}

export default RetailerList;
